import { useEffect, useState } from "react";
import { http, apiError, rp } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/Ui";
import UploadBox from "@/components/UploadBox";
import { Save } from "lucide-react";

const empty = {
  kode_stock: "", deskripsi_produk: "", type_produk: "", spesifikasi_produk: "",
  tanggal_berlaku: "", harga_beli_usd: "", kurs: "", hpp_idr: "",
  harga_jual_a: "", harga_jual_b: "", harga_jual_c: "", catatan: "",
};

const margin = (jual, hpp) => {
  const j = Number(jual) || 0;
  const h = Number(hpp) || 0;
  return j ? (((j - h) / j) * 100).toFixed(2) : "0.00";
};

export default function HppEntry() {
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [products, setProducts] = useState([]);

  const load = () => http.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const idr = (Number(f.harga_beli_usd) || 0) * (Number(f.kurs) || 0);

  const autofill = async (kode) => {
    if (!kode) return;
    try {
      const { data } = await http.get(`/products/${kode.toUpperCase()}`);
      setF((s) => ({ ...s, deskripsi_produk: data.deskripsi_produk || "", type_produk: data.type_produk || "",
        spesifikasi_produk: data.spesifikasi_produk || "" }));
      toast.success(`Data produk ${kode.toUpperCase()} terisi otomatis`);
    } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/hpp", {
        ...f, kode_stock: f.kode_stock.toUpperCase(),
        harga_beli_usd: Number(f.harga_beli_usd) || 0, kurs: Number(f.kurs) || 0,
        harga_beli_idr: idr, hpp_idr: Number(f.hpp_idr) || 0,
        harga_jual_a: Number(f.harga_jual_a) || 0, harga_jual_b: Number(f.harga_jual_b) || 0,
        harga_jual_c: Number(f.harga_jual_c) || 0,
      });
      toast.success(`Tersimpan. Margin A ${data.data.gross_margin_a}% · B ${data.data.gross_margin_b}% · C ${data.data.gross_margin_c}%`);
      setF(empty); load();
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const inp = "w-full h-10 px-3 border border-neutral-300 bg-white outline-none focus:ring-2 focus:ring-[#4338CA] focus:border-transparent text-sm";
  const lbl = "text-[11px] font-semibold uppercase tracking-wide text-[#525252]";
  const fld = ({ label, tid, k, type = "text", ...rest }) => (
    <div>
      <label className={lbl}>{label}</label>
      <input data-testid={tid} type={type} className={inp + (type === "number" ? " mono text-right" : "")}
        value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} {...rest} />
    </div>
  );

  return (
    <div data-testid="hpp-entry-page">
      <PageHeader subtitle="Dept Keuangan · Kantor Pusat" title="Entry & Upload HPP + Harga Jual" testId="hpp-entry-header" />
      <div className="p-6 sm:p-8 space-y-4">
        <UploadBox endpoint="/hpp/upload" templateKind="hpp" onDone={load} testId="hpp-upload" />

        <form onSubmit={submit} className="erp-panel p-5 space-y-5" data-testid="hpp-entry-form">
          <div className="mono text-[11px] uppercase tracking-widest text-[#525252]">
            Perhitungan Awal / Update HPP
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className={lbl}>Kode Stock</label>
              <input data-testid="hpp-kode-input" required list="prodlist3" className={inp + " uppercase"}
                value={f.kode_stock} onChange={(e) => setF({ ...f, kode_stock: e.target.value })}
                onBlur={(e) => autofill(e.target.value)} placeholder="KSFO022" />
              <datalist id="prodlist3">
                {products.map((p) => <option key={p.kode_stock} value={p.kode_stock}>{p.deskripsi_produk}</option>)}
              </datalist>
            </div>
            {fld({ label: "Deskripsi Produk", tid: "hpp-deskripsi-input", k: "deskripsi_produk" })}
            {fld({ label: "Type Produk", tid: "hpp-type-input", k: "type_produk" })}
            {fld({ label: "Spesifikasi Produk", tid: "hpp-spec-input", k: "spesifikasi_produk" })}

            {fld({ label: "Tanggal Berlaku", tid: "hpp-tanggal-input", k: "tanggal_berlaku", type: "date" })}
            {fld({ label: "Harga Beli USD", tid: "hpp-usd-input", k: "harga_beli_usd", type: "number", step: "0.01", placeholder: "0" })}
            {fld({ label: "Kurs USD-IDR", tid: "hpp-kurs-input", k: "kurs", type: "number", placeholder: "16000" })}
            <div>
              <label className={lbl}>Harga Beli Rupiah</label>
              <div data-testid="hpp-idr-display" className="h-10 px-3 border border-neutral-200 bg-neutral-50 flex items-center justify-end mono text-sm">{rp(idr)}</div>
            </div>

            {fld({ label: "HPP Rupiah", tid: "hpp-hpp-input", k: "hpp_idr", type: "number", placeholder: "0" })}
            <div>
              <label className={lbl}>Harga Jual Kategori A</label>
              <input data-testid="hpp-jual-a-input" type="number" className={inp + " mono text-right"}
                value={f.harga_jual_a} onChange={(e) => setF({ ...f, harga_jual_a: e.target.value })} placeholder="0" />
              <div data-testid="hpp-margin-a" className="mono text-xs mt-1 text-[#16A34A]">GM A: {margin(f.harga_jual_a, f.hpp_idr)}%</div>
            </div>
            <div>
              <label className={lbl}>Harga Jual Kategori B</label>
              <input data-testid="hpp-jual-b-input" type="number" className={inp + " mono text-right"}
                value={f.harga_jual_b} onChange={(e) => setF({ ...f, harga_jual_b: e.target.value })} placeholder="0" />
              <div data-testid="hpp-margin-b" className="mono text-xs mt-1 text-[#16A34A]">GM B: {margin(f.harga_jual_b, f.hpp_idr)}%</div>
            </div>
            <div>
              <label className={lbl}>Harga Jual Kategori C</label>
              <input data-testid="hpp-jual-c-input" type="number" className={inp + " mono text-right"}
                value={f.harga_jual_c} onChange={(e) => setF({ ...f, harga_jual_c: e.target.value })} placeholder="0" />
              <div data-testid="hpp-margin-c" className="mono text-xs mt-1 text-[#16A34A]">GM C: {margin(f.harga_jual_c, f.hpp_idr)}%</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 border-t border-neutral-200 pt-4">
            <input data-testid="hpp-catatan-input" className={inp + " sm:max-w-sm"} placeholder="Catatan"
              value={f.catatan} onChange={(e) => setF({ ...f, catatan: e.target.value })} />
            <button data-testid="hpp-submit-button" disabled={busy}
              className="h-10 px-6 bg-[#4338CA] text-white text-xs uppercase tracking-wide flex items-center justify-center gap-2 hover:bg-[#3730A3] transition-colors disabled:opacity-60">
              <Save className="h-3.5 w-3.5" /> Simpan HPP
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
